import Navigation from '@/components/Navigation'
import Footer from '@/components/Footer'
import { usePageMeta } from '@/hooks/usePageMeta'

const AboutPage = () => {
  usePageMeta({
    title: 'About | SPOKE Film',
    description: "The story behind SPOKE — Rudy Pospisil's extraordinary journey and the crisis inside firehouses that nobody is talking about.",
  })
  return (
    <>
      <Navigation />

      {/* Hero */}
      <section className="relative min-h-[40vh] bg-[#0a0f1a] flex items-end pt-24 pb-16 px-6">
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-[#0a0f1a]"></div>
        <div className="relative z-10 max-w-4xl mx-auto w-full">
          <div className="w-12 h-1 bg-red-600 mb-6"></div>
          <h1 className="text-5xl md:text-7xl font-montserrat font-black uppercase tracking-wide text-white leading-none">
            About <span className="text-red-600">SPOKE</span>
          </h1>
        </div>
      </section>

      {/* The Film */}
      <section className="bg-[#0a0f1a] py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-montserrat font-black uppercase tracking-wide text-red-600 mb-6">
            The Film
          </h2>
          <p className="text-gray-300 font-poppins font-light text-lg leading-relaxed mb-5">
            SPOKE is more than just a film — it's a heartfelt tribute to the brave men and women who serve as firefighters.
            Through the universal language of cycling and community, we tell a story that resonates
            with anyone who has ever faced adversity and found strength in unexpected places.
          </p>
          <p className="text-gray-300 font-poppins font-light text-lg leading-relaxed">
            The film explores themes of brotherhood, resilience, and the healing power of shared passion. It shows how
            these everyday heroes find balance between their high-stakes professional lives and their personal journeys
            toward mental and physical wellness.
          </p>
        </div>
      </section>

      {/* Production + Message */}
      <section className="bg-black py-16 px-6">
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-[2px] bg-red-600"></div>
              <h3 className="font-montserrat font-black uppercase tracking-widest text-white text-sm">Production</h3>
            </div>
            <p className="text-gray-300 font-poppins font-light leading-relaxed">
              Shot with authentic locations and real firefighters, SPOKE brings an unprecedented level of realism
              to the screen. Our production team worked closely with fire departments to ensure accuracy and respect
              in every scene.
            </p>
          </div>
          <div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-[2px] bg-yellow-400"></div>
              <h3 className="font-montserrat font-black uppercase tracking-widest text-white text-sm">Message</h3>
            </div>
            <p className="text-gray-300 font-poppins font-light leading-relaxed">
              Behind every uniform is a human being with hopes, fears, and dreams. SPOKE reveals the person behind
              the badge, showing that heroes are made not just in moments of crisis, but in daily acts of courage
              and compassion.
            </p>
          </div>
        </div>
      </section>

      {/* Official Documents */}
      <section className="bg-[#0a0f1a] py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-[2px] bg-red-600"></div>
            <h3 className="font-montserrat font-black uppercase tracking-widest text-white text-sm">Official Documents</h3>
          </div>
          <p className="text-gray-400 font-poppins font-light mb-6">
            For press, database verification, and official inquiries.
          </p>
          <a
            href="/Spoke_Official_Production_Statement.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 transition-colors duration-300 font-montserrat font-black uppercase tracking-wider text-sm"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Production Statement (PDF)
          </a>
        </div>
      </section>

      {/* Subscribe section */}
      <section className="bg-black py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-12 h-1 bg-yellow-400 mx-auto mb-8"></div>
          <h2 className="text-4xl md:text-5xl font-montserrat font-black uppercase tracking-wide text-white mb-4">
            Stay Close to the Story
          </h2>
          <p className="text-gray-400 font-poppins font-light text-lg mb-14">
            Join the SPOKE community and be part of what comes next.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-14 text-left">
            <div className="border-t-2 border-red-600 pt-6">
              <p className="text-yellow-400 font-montserrat font-black uppercase tracking-widest text-xs mb-3">01</p>
              <h3 className="text-white font-montserrat font-black uppercase tracking-wide text-lg mb-3">
                Screening Alerts
              </h3>
              <p className="text-gray-400 font-poppins font-light text-sm leading-relaxed">
                Be the first to know when SPOKE comes to a city near you. Get dates, venues, and early-access tickets before anyone else.
              </p>
            </div>
            <div className="border-t-2 border-red-600 pt-6">
              <p className="text-yellow-400 font-montserrat font-black uppercase tracking-widest text-xs mb-3">02</p>
              <h3 className="text-white font-montserrat font-black uppercase tracking-wide text-lg mb-3">
                Behind the Lens
              </h3>
              <p className="text-gray-400 font-poppins font-light text-sm leading-relaxed">
                Exclusive updates, filmmaker notes, and untold stories straight from the firehouse — content you won't find anywhere else.
              </p>
            </div>
            <div className="border-t-2 border-red-600 pt-6">
              <p className="text-yellow-400 font-montserrat font-black uppercase tracking-widest text-xs mb-3">03</p>
              <h3 className="text-white font-montserrat font-black uppercase tracking-wide text-lg mb-3">
                Early Supporter Recognition
              </h3>
              <p className="text-gray-400 font-poppins font-light text-sm leading-relaxed">
                Subscribers are recognised as early supporters of the film and will be the first to hear about opportunities to get involved.
              </p>
            </div>
          </div>

          <a
            href="https://filmabee-studios.kit.com/spoke"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-red-600 hover:bg-red-700 transition-colors duration-300 px-10 py-4"
          >
            <span className="text-yellow-400 font-montserrat font-black uppercase tracking-wide text-lg md:text-xl">
              Subscribe Now
            </span>
          </a>
        </div>
      </section>

      <Footer />
    </>
  )
}

export default AboutPage
